import * as React from 'react'
import { useMemo, useState } from 'react'
import { Billable, TurnOverItem } from '../../config/registration/timeConfig'
import {
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    Button,
    Field,
    InteractionTag,
    InteractionTagPrimary,
    makeStyles,
    tokens,
} from '@fluentui/react-components'
import { MoneyDto } from '../../config/i18nConfig'
import { IOverviewItemDto } from '../../config/fileConfig'
import { UserListItemDto, UserStatus } from '../../config/userConfig'
import { TimeListItemDto } from '../../config/registrationConfig'
import { useTranslation } from 'react-i18next'
import { LocalizedValueSetting, ModuleType, TimeSuggestionDescriptionOptionEnum } from '../../context/GlobalContext'
import { BaseOverviewCount, BaseOverviewLineItemProps, parseFilterValuesToParams, useOverviewStyles } from '../BaseOverview/BaseOverview'
import { AttributeContextType, AttributeEntityDto, AttributeViewType, EntityAttributeType } from '../../hooks/useAttributes'
import { BaseOverviewFilterProps, BaseOverviewFilterType, generateFromDate, generateToDate } from '../BaseOverview/BaseOverviewFilter'
import { BaseOverviewSortingType } from '../BaseOverview/BaseSortMenu'
import { useFormikContext } from 'formik'
import useModules from '../../hooks/module/useModules'
import DateRange from '../../components/DatePicker/DateRange'
import FilePicker from '../../components/TagPicker/FilePicker'
import UserPicker from '../../components/TagPicker/UserPicker'
import AttributesList from '../../components/Form/Attributes/AttributesList'
import { SortDirection } from '../../config/settingConfig'
import useAxios from '../../hooks/useAxios'
import { AxiosResponse } from 'axios'
import EnumPicker, { EnumPickerLabel } from '../../components/TagPicker/EnumPicker'
import { formatDifferenceInTime, parseDateTime } from '../../helpers/dateHelper'
import { getTimeTurnover, TimeFilterType } from '../../v2/pages/Time/BaseTimeOverview'
import TimeSuggestionListItem from './TimeSuggestionListItem'
import BaseHeaderItem from '../BaseOverview/BaseHeaderItem'
import { useSettings } from '../../hooks/useSettings'
import { CalendarMonthRegular } from '@fluentui/react-icons'
import useRoles, { rolesTimes } from '../../hooks/useRoles'
import useUser from '../../hooks/useUser'
import { TimeDivisorSetting, useTimeDivisorSetting } from '../../hooks/file/useTimeDivisorSetting'
import BaseMutableOverview, { BaseMutableOverviewActionButtonProps } from '../BaseMutableOverview/BaseMutableOverview'
import TimeSuggestionMutationDialogBody, { TrackTimeSuggestionDto } from '../../v2/components/Dialog/Registration/TimeSuggestionMutationDialogBody'
import HammockDialog, { useDialogStyles } from '../../v2/components/Dialog/HammockDialog'
import PreviewMessageDialogBody, { PreviewMessageDialogBodyProps } from '../../v2/components/Dialog/Message/PreviewMessageDialogBody'
import { ExtendedEmailOverviewDto } from '../../v2/pages/File/ViewFileTabs/FileEmailOverview'
import { Destination } from '../../context/AxiosContext'

export const useItemStyles = makeStyles({
    listTimeSuggestionItem: {
        gridTemplateColumns: 'var(--baseOverviewIconWidth) auto',
    },
    listTimeSuggestionItemSelected: {
        backgroundColor: 'var(--colorBrandBackground2Hover)!important',
    },
    listInvoiceItemRow1: {
        display: 'grid',
        gridTemplateColumns: 'auto 120px',
        gap: tokens.spacingVerticalS,
    },
    listInvoiceItemRow1Small: {
        display: 'grid',
        gridTemplateColumns: 'auto',
        gap: tokens.spacingVerticalS,
    },
    listInvoiceItemRow2: {
        display: 'grid',
        gridTemplateColumns: 'auto var(--baseOverviewFileWidth)',
        gap: tokens.spacingVerticalS,
    },
    listInvoiceItemRow2Small: {
        display: 'grid',
        gridTemplateColumns: 'auto',
        gap: tokens.spacingVerticalS,
    },
    listInvoiceItemRow3Small: {
        display: 'grid',
        gridTemplateColumns: '120px auto var(--baseOverviewFileWidth)',
        gap: tokens.spacingVerticalS,
    },
})

export enum TimeSuggestionStatus {
    ToReview = 'TO_REVIEW',
    NoTracking = 'NO_TRACKING',
    Tracked = 'TRACKED',
}

// The order matters for dropdowns.
export const TimeSuggestionStatusLabels = new Map<TimeSuggestionStatus, EnumPickerLabel>([
    [TimeSuggestionStatus.ToReview, { name: 'time.suggestion.status.toreview', disabled: false }],
    [TimeSuggestionStatus.Tracked, { name: 'time.suggestion.status.tracked', disabled: false }],
    [TimeSuggestionStatus.NoTracking, { name: 'time.suggestion.status.notracking', disabled: false }],
])

export type TimeSuggestionOverviewDto = {
    id: string
    file: IOverviewItemDto
    user: UserListItemDto
    time: TimeListItemDto
    internetMessageId: string
    emailCreated: string
    emailSent: string
    senderName: string
    senderEmail: string
    recipientsName: string
    recipientsEmail: string
    emailInbound: boolean
    subject: string
    status: TimeSuggestionStatus
}

export type ExtendedTimeSuggestionOverviewDto = TimeSuggestionOverviewDto &
    TurnOverItem & {
        // These fields are calculated during runtime and not actually returned by the API.
        title: string
        subTitle: string
        turnover: MoneyDto // Expressed in American number notation (0.00).
    }

type TimeSuggestionOverviewProps = BaseOverviewLineItemProps<ExtendedTimeSuggestionOverviewDto, TimeSuggestionFilterType, TimeSuggestionSortingType>

export type BaseTimeSuggestionOverviewProps = Partial<TimeSuggestionOverviewProps> & {
    baseUrl: string

    fileId?: string
    hideFileField?: boolean
}

export type BaseTimeSuggestionLineItemProps = BaseTimeSuggestionOverviewProps &
    BaseOverviewLineItemProps<ExtendedTimeSuggestionOverviewDto, TimeSuggestionFilterType, TimeSuggestionSortingType> & {
        onPreviewClick?: (item: ExtendedTimeSuggestionOverviewDto) => void
    }

export type TimeSuggestionFilterType = AttributeEntityDto &
    BaseOverviewFilterType & {
        from?: string
        to?: string
        files?: string[]
        itemUsers?: string[]
        statuses?: TimeSuggestionStatus[]
    }

export type TimeSuggestionSorting = {
    id: string
    emailSent: string
}

export type TimeSuggestionSortingType = BaseOverviewSortingType<TimeSuggestionSorting>

type BaseTimeSuggestionOverviewFilterProps = BaseTimeSuggestionOverviewProps &
    BaseOverviewFilterProps<ExtendedTimeSuggestionOverviewDto, TimeSuggestionFilterType, TimeSuggestionSortingType>

/**
 * Converts an OverviewDto to a TrackTimeSuggestionDto. This is used for filling the dialog upon click.
 *
 * @param item the source item coming from the server.
 * @param timeDivisor The divisor to use on the duration. Rounds the duration to the configured setting values.
 * @param language the language currently used by the user.
 * @param setting the settings containing the description template used by the customer.
 * @param currentUserId Sets the current user in case there is no stored user.
 * @return the converted item used in the dialog.
 */
export const mapOverviewItemToTrackTimeSuggestionDto = (
    item: TimeSuggestionOverviewDto,
    timeDivisor: TimeDivisorSetting,
    language: string,
    setting?: LocalizedValueSetting<string, LocalizedValueSetting<TimeSuggestionDescriptionOptionEnum>[]>[],
    currentUserId?: string,
): Partial<TrackTimeSuggestionDto> => ({
    timeSuggestionId: item.id,
    fileId: item.file.id,
    date: parseDateTime(item.emailSent).toFormat('yyyy-LL-dd'),
    description: getOverviewItemDescription(item, language, setting),
    duration: timeDivisor.initialDuration,
    emailInbound: item.emailInbound || true,
    userId: item.user.id ?? currentUserId,
})

/**
 * Determines the description of the OverviewDto. Currently only used for e-mails, but can be extended for other types.
 *
 * @param item the item used to determine the description.
 * @param language the language currently used by the user.
 * @param setting the settings containing the description template used by the customer.
 */
export const getOverviewItemDescription = (
    item: TimeSuggestionOverviewDto,
    language: string,
    setting?: LocalizedValueSetting<string, LocalizedValueSetting<TimeSuggestionDescriptionOptionEnum>[]>[],
): string => {
    const time = parseDateTime(item.emailSent).toFormat('HH:mm')

    let description =
        setting
            ?.find(name => name.key.toLowerCase() === language.toLowerCase())
            ?.value?.find(
                option =>
                    option.key === (item.emailInbound ? TimeSuggestionDescriptionOptionEnum.EmailIn : TimeSuggestionDescriptionOptionEnum.EmailOut),
            ).value || ''

    description = description.replaceAll('${time}', time)
    description = description.replaceAll('${subject}', item.subject)
    description = description.replaceAll('${sender}', item.senderName)
    description = description.replaceAll('${recipients}', item.recipientsName)
    description = description.replaceAll('${file.name}', item.file.name)
    description = description.replaceAll('${file.reference}', item.file.reference)
    return description
}

export function TimeSuggestionFilter(props: BaseTimeSuggestionOverviewFilterProps) {
    const { values, setFieldValue } = useFormikContext<TimeSuggestionFilterType>()
    const modules = useModules()
    const { t: translate } = useTranslation()

    const orientation = useMemo(() => (props.viewportMedium ? 'vertical' : 'horizontal'), [props.viewportMedium])

    const updateFieldValue = (field: string, value: any, shouldValidate?: boolean) => {
        setFieldValue('tag', undefined, false).then(() => setFieldValue(field, value, shouldValidate))
    }

    return (
        <>
            <DateRange
                orientation={orientation}
                startValue={values.from}
                endValue={values.to}
                changeStartDate={date => updateFieldValue('from', date)}
                changeEndDate={date => updateFieldValue('to', date)}
                showLabel
            />
            <Field label={translate('registration.filter.user')} orientation={orientation}>
                <UserPicker
                    selectedOptions={values.itemUsers || []}
                    onOptionsSelect={(_, selectedOptions) => updateFieldValue('itemUsers', selectedOptions)}
                />
            </Field>
            {!props.hideFileField && (
                <Field label={translate('time.suggestion.filter.file')} orientation={orientation}>
                    <FilePicker
                        selectedOptions={values.files || []}
                        onOptionsSelect={(_, selectedOptions) => updateFieldValue('files', selectedOptions)}
                    />
                </Field>
            )}
            <Field label={translate('time.suggestion.filter.status')} orientation={orientation}>
                <EnumPicker<TimeSuggestionStatus>
                    options={[...TimeSuggestionStatusLabels.keys()]}
                    labels={TimeSuggestionStatusLabels}
                    placeholderKey={'time.suggestion.status.placeholder'}
                    selectedOptions={values.statuses || []}
                    onOptionsSelect={(_, selectedOptions: TimeSuggestionStatus[]) => updateFieldValue('statuses', selectedOptions)}
                />
            </Field>
            {modules.has(ModuleType.Attributes) && (
                <Accordion collapsible>
                    <AccordionItem value="attributes">
                        <AccordionHeader>{translate('invoice.filter.attributes')}</AccordionHeader>
                        <AccordionPanel>
                            <AttributesList
                                orientation={orientation}
                                entityType={EntityAttributeType.File}
                                context={[AttributeContextType.File]}
                                viewType={AttributeViewType.Filter}
                                entity={values}
                                setFieldValue={(field, value) => setFieldValue(values[field], value)}
                            />
                        </AccordionPanel>
                    </AccordionItem>
                </Accordion>
            )}
        </>
    )
}

const filterTagItems: TimeSuggestionFilterType[] = [
    {
        from: generateFromDate('month'),
        to: generateToDate('month'),
        tag: 'month',
        icon: <CalendarMonthRegular />,
        title: 'time.suggestion.filter.month',
    },
    {
        from: generateFromDate('year'),
        to: generateToDate('year'),
        tag: 'year',
        icon: <CalendarMonthRegular />,
        title: 'time.suggestion.filter.year',
    },
]

const headerSortingItems: TimeSuggestionSortingType[] = [
    {
        property: ['emailSent', 'id'],
        direction: SortDirection.Ascending,
        descriptionKey: 'sorting.dateasc',
    },
    {
        property: ['emailSent', 'id'],
        direction: SortDirection.Descending,
        descriptionKey: 'sorting.datedesc',
        default: true,
    },
]

function HeaderFilterTags(props: TimeSuggestionOverviewProps) {
    const { values, setValues } = useFormikContext<TimeFilterType>()
    const overviewStyles = useOverviewStyles()
    const { t: translate } = useTranslation()

    return (
        <div className={overviewStyles.flexGapXS}>
            {filterTagItems.map((tag, index) => (
                <InteractionTag
                    key={index}
                    selected={values.tag === tag.tag}
                    onClick={() =>
                        setValues({
                            ...props.defaultFilterValues,
                            ...tag,
                        })
                    }
                >
                    <InteractionTagPrimary icon={tag.icon}> {translate(tag.title)}</InteractionTagPrimary>
                </InteractionTag>
            ))}
        </div>
    )
}

export function HeaderActionsButtons() {
    const overviewStyles = useOverviewStyles()

    return (
        <>
            <Button appearance={'secondary'} className={overviewStyles.hidden} />
            <Button appearance={'secondary'} className={overviewStyles.hidden} />
            <Button appearance={'secondary'} className={overviewStyles.hidden} />
        </>
    )
}

export default function BaseTimeSuggestionOverview(props: BaseTimeSuggestionOverviewProps) {
    const axios = useAxios()
    const overviewStyles = useOverviewStyles()
    const dialogStyles = useDialogStyles()
    const itemStyles = useItemStyles()
    const roles = useRoles()
    const { currentUser } = useUser()
    const { settings } = useSettings()
    const timeDivisor = useTimeDivisorSetting()
    const { i18n } = useTranslation()

    //We only need to fetch the ID
    const [openPreviewMessage, setOpenPreviewMessage] = useState<ExtendedEmailOverviewDto>(undefined)
    const resolveAndPreview = (item: ExtendedTimeSuggestionOverviewDto) => {
        setOpenPreviewMessage({
            id: item.internetMessageId,
        } as ExtendedEmailOverviewDto)
    }

    const fixedFilterValues = useMemo<Partial<TimeSuggestionFilterType>>(
        () => ({
            itemUsers: roles.any(rolesTimes) && currentUser?.status === UserStatus.Enabled ? [currentUser?.id] : [],
            ...props.fixedFilterValues,
        }),
        [],
    )

    const defaultFilterValues = useMemo<TimeSuggestionFilterType>(
        () => ({
            from: '',
            to: '',
            itemUsers: roles.any(rolesTimes) && currentUser?.status === UserStatus.Enabled ? [currentUser?.id] : [],
            statuses: [TimeSuggestionStatus.ToReview],
            attributes: [],
            query: '',
            tag: 'month',
            ...props.fixedFilterValues,
            ...props.defaultFilterValues,
        }),
        [props.fixedFilterValues, props.defaultFilterValues],
    )

    const countItems = (_column: number, filterValues?: TimeSuggestionFilterType): Promise<BaseOverviewCount> => {
        return axios
            .get<BaseOverviewCount>('/time-suggestion/count', { params: parseFilterValuesToParams(filterValues) })
            .then((response: AxiosResponse<BaseOverviewCount>): BaseOverviewCount => response.data)
    }

    const loadItems = (
        _column: number,
        filterValues?: TimeSuggestionFilterType,
        sortingValues?: TimeSuggestionSortingType,
        start?: number,
        size?: number,
    ): Promise<ExtendedTimeSuggestionOverviewDto[]> => {
        return axios
            .get<TimeSuggestionOverviewDto[]>('/time-suggestion', {
                params: { ...parseFilterValuesToParams(filterValues, sortingValues, start, size) },
            })
            .then((response: AxiosResponse<TimeSuggestionOverviewDto[]>): ExtendedTimeSuggestionOverviewDto[] =>
                (response?.data || []).map((item: TimeSuggestionOverviewDto) => {
                    const extendedItem: ExtendedTimeSuggestionOverviewDto = {
                        billable: Billable.Billable,
                        duration: formatDifferenceInTime(item.emailCreated, item.emailSent, timeDivisor.initialDuration),
                        title: item.emailInbound ? item.senderName : item.recipientsName || '',
                        subTitle: item.subject || '',
                        turnover: undefined,
                        ...item,
                    }

                    return {
                        ...extendedItem,
                        turnover: getTimeTurnover({ amount: item.file.rate, currency: item.file.currency }, extendedItem),
                    }
                }),
            )
    }

    return (
        <>
            <HammockDialog<PreviewMessageDialogBodyProps>
                open={openPreviewMessage !== undefined}
                setOpen={(open: boolean) => setOpenPreviewMessage(open ? openPreviewMessage : undefined)}
                dialogBody={PreviewMessageDialogBody}
                surfaceProps={{
                    className: dialogStyles.dialogHeight,
                }}
                dialogBodyProps={() => ({
                    message: openPreviewMessage,
                    destination: Destination.Email,
                })}
            />

            <BaseMutableOverview<
                ExtendedTimeSuggestionOverviewDto,
                TimeSuggestionFilterType,
                TimeSuggestionSortingType,
                BaseOverviewCount,
                BaseMutableOverviewActionButtonProps<ExtendedTimeSuggestionOverviewDto>,
                any,
                Partial<TrackTimeSuggestionDto>
            >
                fixedFilterValues={fixedFilterValues}
                defaultFilterValues={defaultFilterValues}
                batchSizeItems={50}
                countItems={countItems}
                loadItems={loadItems}
                listItemHeaderContent={BaseHeaderItem}
                listItemHeaderClassnames={[overviewStyles.listBaseHeaderItem]}
                listItemContent={(props: BaseTimeSuggestionLineItemProps) => <TimeSuggestionListItem {...props} onPreviewClick={resolveAndPreview} />}
                listItemClassnames={() => [itemStyles.listTimeSuggestionItem]}
                headerDefaultFilterTags={HeaderFilterTags}
                headerSortingItems={headerSortingItems}
                headerActionsButtons={HeaderActionsButtons}
                filterContent={TimeSuggestionFilter}
                filterFromJson={values => ({
                    ...values,
                    from: values.tag ? generateFromDate(values.tag) : values.from,
                    to: values.tag ? generateToDate(values.tag) : values.to,
                })}
                filterToJson={values => ({
                    ...values,
                    from: values.tag ? '' : values.from,
                    to: values.tag ? '' : values.to,
                    icon: undefined,
                    title: undefined,
                })}
                inputPlaceholderKey={'time.suggestion.filter.search'}
                mutationDialogFormProps={item => ({
                    initialValues: mapOverviewItemToTrackTimeSuggestionDto(
                        item,
                        timeDivisor,
                        i18n.language,
                        settings.timeSuggestionDescription,
                        currentUser?.id,
                    ),
                    onSubmit: () => {},
                })}
                mutationDialogBody={TimeSuggestionMutationDialogBody}
                {...props}
            />
        </>
    )
}
